import React from 'react';

// Styles & Assets
import styles from './PatientAssociatedUsers.module.css';
import { MdCheckCircle } from 'react-icons/md';
import { ReactComponent as PersonAdd } from '../../../assets/icons/person-add.svg';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useState, useEffect } from 'react';
import useHttp from '../../../hooks/useHttp';

// Components
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import Modal from '../../UI/Modal/Modal';
import DialogNewAssociatedUser from '../DialogNewAssociatedUser/DialogNewAssociatedUser';

// Interfaces
import { patientCarerWithCarer } from '../../../interfaces/Carer';

// Language
import { useTranslation } from 'react-i18next';
import ContactPersonButton from '../ContactPersonButton/ContactPersonButton';
import PatientInfoTitle from '../PatientInfoTitle/PatientInfoTitle';

const PatientAssociatedUsers: React.FC<{
    disabledAdding: boolean
    patientID: number
}> = ({ disabledAdding, patientID }) => {

    const { t } = useTranslation();

    const [showNewAssociatedUserDialog, setShowNewAssociatedUserDialog] = useState(false);
    const [selectedCarer, setSelectedCarer] = useState<null | patientCarerWithCarer>(null);
    const [patientCarers, setPatientCarers] = useState<patientCarerWithCarer[]>([]);

    const { isLoading, error, sendRequest } = useHttp<patientCarerWithCarer[]>();
    const { sendRequest: deleteCarer } = useHttp();

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'patients_carer/all',
            method: 'POST',
            body: {
                params: {},
                filter: { patient_id: patientID }
            }
        }, (data) => setPatientCarers(data));
    }, [patientID, sendRequest]);

    const handleOpenUserDialog = (patientCarer: patientCarerWithCarer | null) => {
        setSelectedCarer(patientCarer);
        setShowNewAssociatedUserDialog(true);
    };

    const setCarer = (carer: patientCarerWithCarer, addNew: boolean) => {
        if (addNew) setPatientCarers(oldCarers => [...oldCarers, carer]);
        else {
            setPatientCarers(oldCarers => oldCarers.map(oldCarer => {
                if (oldCarer.carer_id !== carer.carer_id) return oldCarer;
                return carer;
            }));
        }
    };

    const handleDeleteCarer = (patientCarer: patientCarerWithCarer) => {
        deleteCarer({
            url: getEnvironmentApiUrl() + 'patients_carer/deleteAll',
            method: 'POST',
            body: {
                carer_id: patientCarer?.carer_id,
                patient_id: patientCarer?.patient_id
            }
        }, () => setPatientCarers(oldCarers =>
            oldCarers.filter(oldCarer => !(oldCarer.carer_id === patientCarer.carer_id && oldCarer.patient_id === patientCarer.patient_id))
        ));
    };

    const mainCarer = patientCarers.find(carer => carer.is_main_carer);

    const getSubtitlesCarer = (patientCarer: patientCarerWithCarer) => {
        return [
            patientCarer.is_formal_carer ? t('formal_carer') : t('informal_carer'),
            patientCarer.carer?.mobile_phone ?? patientCarer.carer?.home_phone ?? '',
            patientCarer.carer?.comment ?? '',
        ].filter(s => s !== '');
    };

    return (
        <div>
            <PatientInfoTitle
                title={t('contact_people')}
                onClick={() => handleOpenUserDialog(null)}
                disabled={disabledAdding}
                size={36}
            >
                <PersonAdd />
            </PatientInfoTitle>
            <ErrorSpinnerWrapper
                error={error}
                loading={isLoading}
            >
                <div className={styles.carersContainer}>
                    {patientCarers.map(patientCarer => {
                        return (
                            <ContactPersonButton
                                key={patientCarer.carer_id}
                                handleDeleteContact={() => handleDeleteCarer(patientCarer)}
                                handleClickContact={() => handleOpenUserDialog(patientCarer)}
                                title={<>
                                    {patientCarer.carer?.first_name + ' ' + patientCarer.carer?.last_name_1}
                                    {patientCarer.is_main_carer &&
                                        <MdCheckCircle />
                                    }
                                </>}
                                subtitles={getSubtitlesCarer(patientCarer)}
                            />
                        );
                    })}
                </div>
            </ErrorSpinnerWrapper>
            <Modal show={showNewAssociatedUserDialog} setShowModal={setShowNewAssociatedUserDialog} secondModal>
                <DialogNewAssociatedUser
                    setShowModal={setShowNewAssociatedUserDialog}
                    patientCarer={selectedCarer}
                    patient_id={patientID}
                    setCarer={setCarer}
                    mainCarer={mainCarer}
                />
            </Modal>
        </div>
    );
};

export default PatientAssociatedUsers;